import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  Grid,
  Group,
  Stack,
  Text,
  ThemeIcon,
  Badge,
  LoadingOverlay,
  Paper,
  Flex,
} from "@mantine/core";
import {
  IconSparkles,
  IconMail,
  IconBrandLinkedin,
  IconMessageCircle,
  IconEye,
  IconUserPlus,
  IconCheck,
  IconLoader2,
  IconSparklesFilled,
} from "@tabler/icons-react";
import { getNodeDetails } from "../flowHorizontal/flowSettings";
import {
  generateWorkflowWithAgent,
  getWorkflowAiJob,
} from "../../../../../api/AgentsAPI";
import { useCampaignStore } from "../../../../../stores/CampaignStore";
import { handleWorkflowGet } from "../flowHorizontal/flowUtils";
import { useFlowStore } from "../../../../../stores/WorkflowStore";
import { useListStore } from "../../../../../stores/ListStore";

// --- ÉTAPES DE GÉNÉRATION ---
const GENERATION_STEPS = [
  "Analyse des paramètres de la campagne...",
  "Définition de la cadence de prospection...",
  "Création du message d'accroche...",
  "Rédaction des relances personnalisées...",
  "Optimisation des délais d'attente...",
  "Finalisation de la stratégie...",
];

const Recommended = () => {
  return (
    <Badge
      color="primary.1"
      c="primary"
      variant="filled"
      radius="sm"
      size="lg"
      mb="md"
      leftSection={<IconSparklesFilled size={14} />}
      style={{ textTransform: "none", fontWeight: 600 }}
    >
      Stratégie recommandée
    </Badge>
  );
};

const StepStrategy = ({ setActive }) => {
  const programData = useCampaignStore((state) => state.programData);
  const setLoadingPage = useFlowStore((s) => s.setLoadingPage);
  const agentId = useCampaignStore((state) => state.agentId);
  // null | 'crosscanal' | 'linkedin' | 'email' | 'sms'
  const [generatingFor, setGeneratingFor] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const [jobId, setJobId] = useState(null);

  // 1. DÉCLENCHEMENT : Appel POST au clic sur le bouton
  const handleGenerate = async (strategy) => {
    setGeneratingFor(strategy);
    setActiveStep(0);

    try {
      // Adaptez le payload (data) selon ce que votre API attend exactement
      const payload = {
        ai_agent_uniqid: agentId,
        name: "generated workflow",
        theme: programData?.context?.value || "général",
        nature: strategy,
        contactlist_ids: programData.contactlist_ids,
        auto_remove_responders: document.getElementById(
          "auto_remove_responders",
        ).checked,
      };
      const res = await generateWorkflowWithAgent(payload);

      // Si l'API retourne 202, on stocke l'uniqid pour commencer le polling
      if (res.data?.uniqid) {
        setJobId(res.data.uniqid);
      } else {
        console.error("Réponse inattendue de l'API", res);
        setGeneratingFor(null);
      }
    } catch (error) {
      console.error("Erreur lors du lancement de la génération", error);
      setGeneratingFor(null);
    }
  };

  // 2. POLLING : Vérification du statut du Job (GET)
  useEffect(() => {
    if (!jobId) return;

    let interval;

    const checkJobStatus = async () => {
      try {
        const res = await getWorkflowAiJob(jobId);
        const job = res.data.job; // Adaptez si votre backend renvoie { job: { ... } }

        if (job.state === "Completed") {
          clearInterval(interval);

          // Récupération de la preview demandée
          const preview = job.state_details?.[0]?.preview;

          // L'API a terminé : on force l'animation visuelle à 100%
          setActiveStep(GENERATION_STEPS.length);

          setActive("workflow");
          setLoadingPage(true);

          // On attend 1 seconde pour que l'utilisateur voie le check vert ✔️
          setTimeout(() => {
            setGeneratingFor(null);
            setJobId(null);
            setActiveStep(0);

            const workflowPayload = preview.workflow_payload;

            handleWorkflowGet({
              workflowProfile: workflowPayload,
              isEdit: false,
              onFinish: ({ newNodes }) => {
                setLoadingPage(false);
              },
            });
          }, 1000);
        } else if (job.state === "Failed" || job.state === "Error") {
          console.error("Le job a échoué côté serveur.");
          clearInterval(interval);
          setGeneratingFor(null);
          setJobId(null);
        }
      } catch (error) {
        console.error("Erreur pendant le polling", error);
        clearInterval(interval);
        setGeneratingFor(null);
        setJobId(null);
      }
    };

    // On lance le polling toutes les 3 secondes (3000ms)
    interval = setInterval(checkJobStatus, 3000);

    // Cleanup à la destruction du composant
    return () => clearInterval(interval);
  }, [jobId]);

  // 3. ANIMATION VISUELLE (Illusion d'avancement pour faire patienter)
  useEffect(() => {
    // Si on est en train de générer ET qu'on n'est pas encore à l'avant-dernière étape
    if (generatingFor && jobId && activeStep < GENERATION_STEPS.length - 1) {
      const delay = 1500 + Math.random() * 10000;
      const t = setTimeout(() => setActiveStep((prev) => prev + 1), delay);
      return () => clearTimeout(t);
    }
    // Si l'animation atteint la fin mais que le polling n'a pas fini,
    // elle s'arrête de monter et le loader continue de tourner sur la dernière étape.
  }, [generatingFor, jobId, activeStep]);

  const isGenerating = generatingFor !== null;

  // --- COMPOSANT CUSTOM POUR LE LOADER ---
  const CustomLoader = () => (
    <Paper radius="xl" p="xl" shadow="xl" withBorder w={400} bg="white">
      <Group gap="sm" mb="lg" justify="center">
        <IconSparkles color="var(--mantine-color-violet-5)" size={24} />
        <Text fw={700} size="lg" ta="center">
          Génération de la stratégie...
        </Text>
      </Group>

      <Stack gap="sm">
        {GENERATION_STEPS.map((stepLabel, index) => {
          const isDone = index < activeStep;
          const isCurrent = index === activeStep;

          return (
            <Group key={index} gap="md" wrap="nowrap">
              <Box
                w={24}
                h={24}
                style={{ display: "grid", placeItems: "center", flexShrink: 0 }}
              >
                {isDone ? (
                  <ThemeIcon color="teal" size={24} radius="xl">
                    <IconCheck size={14} stroke={3} />
                  </ThemeIcon>
                ) : isCurrent ? (
                  <IconLoader2
                    size={24}
                    color="var(--mantine-color-violet-5)"
                    style={{ animation: "spin 2s linear infinite" }}
                  />
                ) : (
                  <Box
                    w={24}
                    h={24}
                    bg="gray.1"
                    style={{
                      borderRadius: "50%",
                      display: "grid",
                      placeItems: "center",
                    }}
                  >
                    <Box
                      w={6}
                      h={6}
                      bg="gray.4"
                      style={{ borderRadius: "50%" }}
                    />
                  </Box>
                )}
              </Box>
              <Text
                size="sm"
                fw={isDone || isCurrent ? 500 : 400}
                c={isDone || isCurrent ? "dark" : "dimmed"}
              >
                {stepLabel}
              </Text>
            </Group>
          );
        })}
      </Stack>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </Paper>
  );

  console.log(programData?.listStats);

  return (
    <Box maw={1150} mx="auto" p="xl" pos="relative">
      {/* OVERLAY DE CHARGEMENT */}
      <LoadingOverlay
        visible={isGenerating}
        zIndex={1000}
        overlayProps={{ radius: "sm", blur: 8 }}
        loaderProps={{ children: <CustomLoader /> }}
      />

      {/* HEADER */}
      <Stack gap={4} mb="xl">
        <Text c="violet.5" fw={600} size="sm">
          Étape 3 · Stratégie
        </Text>
        <Text component="h1" size="xl" fw={700} m={0} style={{ fontSize: 24 }}>
          La stratégie recommandée pour cette audience
        </Text>
        <Text c="dimmed" size="sm">
          Votre agent a défini le mix de canaux le plus performant. Acceptez-la
          ou personnalisez-la selon vos besoins.
        </Text>
      </Stack>

      {/* STRATÉGIE RECOMMANDÉE (Multicanale) */}
      <Card
        withBorder
        radius="xl"
        p="xl"
        mb="lg"
        style={{
          borderColor: "var(--mantine-color-violet-4)",
          borderWidth: 2,
          boxShadow: "0 4px 20px rgba(132, 94, 247, 0.05)",
        }}
      >
        <Flex
          direction={{ base: "column", md: "row" }}
          gap="xl"
          justify="space-between"
          align="flex-start"
        >
          <Box flex={1}>
            {programData?.listStats?.number_of_emails > 0 &&
              programData?.listStats?.number_of_linkedin_url > 0 && (
                <Recommended />
              )}
            <Text fw={700} size="xl" mb={4}>
              Campagne multicanale
            </Text>
            <Text c="dimmed" size="sm" mb="lg">
              Email + LinkedIn - {programData?.listStats?.number_of_contacts}{" "}
              points de contact
            </Text>

            {/* Badges des étapes */}
            <Group gap="xs" mb="xl">
              <Badge
                variant="default"
                radius="md"
                leftSection={<IconEye size={12} />}
                fw={500}
                c={getNodeDetails("linkedin_visit")?.color}
              >
                Visite
              </Badge>
              <Badge
                variant="default"
                radius="md"
                leftSection={<IconMail size={12} />}
                fw={500}
                c={getNodeDetails("email")?.color}
              >
                Email
              </Badge>
              <Badge
                variant="default"
                radius="md"
                leftSection={<IconUserPlus size={12} />}
                fw={500}
                c={getNodeDetails("linkedin_invitation")?.color}
              >
                Connexion
              </Badge>
              <Badge
                variant="default"
                radius="md"
                leftSection={<IconBrandLinkedin size={12} />}
                fw={500}
                c={getNodeDetails("linkedin_message")?.color}
              >
                Message LinkedIn
              </Badge>
              {/* <Badge
                variant="default"
                radius="md"
                leftSection={<IconMessageCircle size={12} />}
                fw={500}
                c={getNodeDetails("sms")?.color}
                style={{ borderStyle: "dashed" }}
              >
                SMS
              </Badge> */}
            </Group>

            <Button
              color="primary"
              radius="xl"
              size="md"
              leftSection={<IconSparkles size={18} />}
              onClick={() => handleGenerate("crosscanal")}
              disabled={
                isGenerating ||
                programData?.listStats?.number_of_emails === 0 ||
                programData?.listStats?.number_of_linkedin_url === 0
              }
              w={300}
            >
              Générer
            </Button>
          </Box>

          {programData?.listStats?.number_of_emails > 0 &&
            programData?.listStats?.number_of_linkedin_url > 0 && (
              <Paper
                withBorder
                radius="md"
                p="md"
                w={{ base: "100%", md: 420 }}
                bg="gray.0"
              >
                <Text size="md" lh={1.5}>
                  <Text component="span" fw={700}>
                    Pourquoi ?{" "}
                  </Text>
                  D'après votre agent et l'audience sélectionnée, alterner email
                  et LinkedIn maximise les taux de réponse tout en respectant
                  les limites d'envoi.
                </Text>
              </Paper>
            )}
        </Flex>
      </Card>

      {/* STRATÉGIES SECONDAIRES */}
      <Grid gutter="lg">
        {/* LinkedIn Uniquement */}
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Card
            withBorder
            radius="xl"
            p="xl"
            h="100%"
            style={{
              display: "flex",
              flexDirection: "column",
              borderColor: "var(--mantine-color-violet-4)",
            }}
          >
            {programData?.listStats?.number_of_emails === 0 &&
              programData?.listStats?.number_of_linkedin_url > 0 && (
                <Recommended />
              )}
            <Group gap="sm" mb="md">
              <ThemeIcon radius="xl" size="lg" color="blue.1" c="blue.6">
                <IconBrandLinkedin size={20} />
              </ThemeIcon>
              <Text fw={700} size="lg">
                Linkedin uniquement
              </Text>
            </Group>
            <Text size="sm" c="dimmed" lh={1.5} mb="xl" style={{ flexGrow: 1 }}>
              Adressez vos prospects exclusivement par LinkedIn grâce à un
              workflow entièrement généré par l'IA.
            </Text>
            <Button
              //   variant="light"
              color="primary"
              radius="xl"
              fullWidth
              onClick={() => handleGenerate("linkedin")}
              disabled={
                isGenerating ||
                programData?.listStats?.number_of_linkedin_url === 0
              }
              size="md"
              leftSection={<IconSparklesFilled size={18} />}
            >
              Générer
            </Button>
          </Card>
        </Grid.Col>

        {/* Email Uniquement */}
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Card
            withBorder
            radius="xl"
            p="xl"
            h="100%"
            style={{
              display: "flex",
              flexDirection: "column",
              borderColor: "var(--mantine-color-violet-4)",
            }}
          >
            {programData?.listStats?.number_of_emails > 0 &&
              programData?.listStats?.number_of_linkedin_url === 0 && (
                <Recommended />
              )}
            <Group gap="sm" mb="md">
              <ThemeIcon radius="xl" size="lg" color="orange.1" c="orange.6">
                <IconMail size={20} />
              </ThemeIcon>
              <Text fw={700} size="lg">
                Email uniquement
              </Text>
            </Group>
            <Text size="sm" c="dimmed" lh={1.5} mb="xl" style={{ flexGrow: 1 }}>
              Adressez vos prospects exclusivement par email grâce à un workflow
              entièrement généré par l'IA.
            </Text>
            <Button
              id="strategy-submit"
              //   variant="light"
              color="primary"
              radius="xl"
              fullWidth
              onClick={() => handleGenerate("email")}
              disabled={
                isGenerating || programData?.listStats?.number_of_emails === 0
              }
              size="md"
              leftSection={<IconSparklesFilled size={18} />}
            >
              Générer
            </Button>
          </Card>
        </Grid.Col>

        {/* SMS Uniquement (GRISÉ) */}
        {/* <Grid.Col span={{ base: 12, md: 4 }}>
          <Card
            withBorder
            radius="xl"
            p="xl"
            h="100%"
            bg="gray.0"
            style={{
              display: "flex",
              flexDirection: "column",
              opacity: 0.6,
              //   borderColor: "var(--mantine-color-violet-4)",
            }}
          >
            <Group gap="sm" mb="md">
              <ThemeIcon radius="xl" size="lg" color="teal.1" c="teal.6">
                <IconMessageCircle size={20} />
              </ThemeIcon>
              <Text fw={700} size="lg" c="dimmed">
                SMS uniquement
              </Text>
            </Group>
            <Text size="sm" c="dimmed" lh={1.5} mb="xl" style={{ flexGrow: 1 }}>
              Adressez vos prospects exclusivement par sms grâce à un workflow
              entièrement généré par l'IA.
            </Text>
            <Button
              variant="light"
              color="gray"
              radius="xl"
              fullWidth
              leftSection={<IconSparkles size={16} />}
              disabled
            >
              Bientôt disponible
            </Button>
          </Card>
        </Grid.Col> */}
      </Grid>
    </Box>
  );
};

export default StepStrategy;
